import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_URL, getAuthHeader } from '../utils/config';
import useStore from '../store/useStore';
import toast from 'react-hot-toast';
import { getSocket } from '../hooks/useSocket';
import EmojiPicker from 'emoji-picker-react';

const Icon = ({ d, size = 18, stroke = 'currentColor', fill = 'none', strokeWidth = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);

const ICONS = {
  back: 'M19 12H5M12 5l-7 7 7 7',
  search: ['M11 19a8 8 0 100-16 8 8 0 000 16z', 'M21 21l-4.35-4.35'],
  image: ['M21 19V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2z', 'M8.5 10a1.5 1.5 0 100-3 1.5 1.5 0 000 3z', 'M21 15l-5-5L5 21'],
  video: ['M23 7l-7 5 7 5V7z', 'M1 5h15a2 2 0 012 2v10a2 2 0 01-2 2H1a2 2 0 01-2-2V7a2 2 0 012-2z'],
  file: ['M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z', 'M14 2v6h6', 'M16 13H8', 'M16 17H8', 'M10 9H8'],
  poll: ['M18 20V10', 'M12 20V4', 'M6 20v-6'],
  heart: 'M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z',
  comment: 'M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z',
  share: ['M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8', 'M16 6l-4-4-4 4', 'M12 2v13'],
  eye: ['M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z', 'M12 9a3 3 0 100 6 3 3 0 000-6z'],
  pin: ['M12 22V12', 'M5 12l7-10 7 10', 'M5 12h14'],
  calendar: ['M3 4h18v18H3z', 'M16 2v4', 'M8 2v4', 'M3 10h18'],
  send: 'M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z',
  trending: ['M23 6l-9.5 9.5-5-5L1 18', 'M17 6h6v6'],
  close: ['M18 6L6 18', 'M6 6l12 12'],
  plus: ['M12 5v14', 'M5 12h14'],
  globe: ['M12 22C6.48 22 2 17.52 2 12S6.48 2 12 2s10 4.48 10 10-4.48 10-10 10z', 'M2 12h20', 'M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z'],
  users: ['M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2', 'M9 7a4 4 0 100 8 4 4 0 000-8z', 'M23 21v-2a4 4 0 00-3-3.87', 'M16 3.13a4 4 0 010 7.75'],
  tag: ['M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z', 'M7 7h.01'],
  chevron: 'M6 9l6 6 6-6',
  trash: ['M3 6h18', 'M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6', 'M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2'],
};

const timeAgo = (date) => {
  if (!date) return '';
  const diff = (Date.now() - new Date(date).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
  if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
  return Math.floor(diff / 86400) + 'd ago';
};

const Avatar = ({ name, url, size = 36, ring = false }) => {
  const colors = ['#0d9488', '#2563eb', '#7c3aed', '#db2777', '#ea580c', '#16a34a'];
  const color = colors[(name?.charCodeAt(0) || 0) % colors.length];
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, background: url ? 'transparent' : color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.38, fontWeight: 700, color: 'white', overflow: 'hidden', border: ring ? '2px solid var(--bg)' : 'none' }}>
      {url ? <img src={url.startsWith('http') ? url : `https://qtalk.qualtechedge.in${url}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" onError={e => e.target.style.display = 'none'} /> : (name?.[0] || '?').toUpperCase()}
    </div>
  );
};

// ── Composer ──────────────────────────────────────────────────────────────────
const Composer = ({ currentUser, onPost, users }) => {
  const [expanded, setExpanded] = useState(false);
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [audience, setAudience] = useState('everyone'); // everyone | department
  const [tagged, setTagged] = useState([]); // [{id, full_name}]
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const [showPoll, setShowPoll] = useState(false);
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [posting, setPosting] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const fileRef = useRef();

  const handleFile = (e, type) => {
    const f = e.target.files[0]; if (!f) return;
    setFile(f); setFileType(type); setShowPoll(false);
    if (type === 'image') setFilePreview(URL.createObjectURL(f));
    else if (type === 'video') setFilePreview(URL.createObjectURL(f));
    else setFilePreview(null);
    e.target.value = '';
  };

  const removeFile = () => { setFile(null); setFilePreview(null); setFileType(null); };

  const addPollOption = () => { if (pollOptions.length < 6) setPollOptions(p => [...p, '']); };
  const removePollOption = (i) => setPollOptions(p => p.filter((_, idx) => idx !== i));
  const updatePollOption = (i, val) => setPollOptions(p => p.map((o, idx) => idx === i ? val : o));

  const handlePost = async () => {
    if (!text.trim() && !file && !(showPoll && pollOptions.filter(Boolean).length >= 2)) return;
    setPosting(true);
    try {
      const fd = new FormData();
      fd.append('content', text);
      fd.append('audience', audience);
      if (tagged.length > 0) fd.append('tagged_users', JSON.stringify(tagged.map(u => u.id)));
      if (showPoll) fd.append('poll_options', JSON.stringify(pollOptions.filter(Boolean)));
      if (file) fd.append('media', file);
      await onPost(fd);
      setText(''); setFile(null); setFilePreview(null); setFileType(null);
      setTagged([]); setShowPoll(false); setPollOptions(['', '']); setExpanded(false); setShowEmoji(false);
    } catch(e) { toast.error('Failed to post'); }
    setPosting(false);
  };

  const filteredUsers = users.filter(u => u.id !== currentUser?.id && u.full_name.toLowerCase().includes(tagSearch.toLowerCase()) && !tagged.find(t => t.id === u.id));
  const [mentionQuery, setMentionQuery] = useState(null);
  const mentionMatches = mentionQuery !== null
    ? users.filter(u => u.id !== currentUser?.id && (u.full_name || '').toLowerCase().includes(mentionQuery.toLowerCase()) && !tagged.find(t => t.id === u.id)).slice(0, 6)
    : [];
  const onTextChange = (val) => {
    setText(val);
    const m = val.match(/@([^\s@]*)$/);
    setMentionQuery(m ? m[1] : null);
  };
  const pickTextMention = (u) => {
    setText(prev => prev.replace(/@([^\s@]*)$/, `@${u.full_name} `));
    setTagged(p => p.find(t => t.id === u.id) ? p : [...p, u]);
    setMentionQuery(null);
  };
  const canPost = text.trim() || file || (showPoll && pollOptions.filter(Boolean).length >= 2);

  return (
    <div style={{ background: 'var(--card)', borderRadius: 20, border: '1px solid var(--border)', marginBottom: 16, overflow: showEmoji ? 'visible' : 'hidden', boxShadow: expanded ? '0 0 0 2px rgba(13,148,136,0.2)' : 'none', transition: 'box-shadow 0.2s' }}>
      {/* Top row */}
      <div style={{ padding: '16px 20px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <Avatar name={currentUser?.full_name} url={currentUser?.avatar_url} size={44} />
        <div style={{ flex: 1, position: 'relative' }}>
          <textarea value={text} onChange={e => onTextChange(e.target.value)}
            onFocus={() => setExpanded(true)}
            placeholder="Share something with the team... (@ to mention)"
            style={{ width: '100%', border: 'none', background: 'var(--card-inner)', borderRadius: 14, padding: '12px 16px', fontSize: 14, color: 'var(--text)', resize: 'none', outline: 'none', fontFamily: 'inherit', lineHeight: 1.6, boxSizing: 'border-box', minHeight: expanded ? 90 : 48, transition: 'min-height 0.2s' }}
            rows={expanded ? 3 : 1} />
          {mentionQuery !== null && mentionMatches.length > 0 && (
            <div style={{ position: 'absolute', top: 44, left: 0, zIndex: 200, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', width: 240, overflow: 'hidden', maxHeight: 220, overflowY: 'auto' }}>
              {mentionMatches.map(u => (
                <div key={u.id} onClick={() => pickTextMention(u)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--card-inner)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <Avatar name={u.full_name} url={u.avatar_url} size={28} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{u.full_name}</div>
                    {u.designation && <div style={{ fontSize: 11, color: 'var(--muted)' }}>{u.designation}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
          <button onClick={() => setShowEmoji(s => !s)} style={{ position: 'absolute', right: 10, top: 10, background: 'none', border: 'none', cursor: 'pointer', color: '#f59e0b', display: 'flex', padding: 2, zIndex: 10 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:18,height:18}}><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
          </button>
          {showEmoji && (
            <div style={{ position: 'absolute', top: 44, right: 0, zIndex: 999, boxShadow: '0 8px 32px rgba(0,0,0,0.25)', borderRadius: 16, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
              <EmojiPicker onEmojiClick={e => { setText(prev => prev + e.emoji); setShowEmoji(false); }} height={320} width={300} skinTonesDisabled previewConfig={{showPreview:false}} />
            </div>
          )}
        </div>
      </div>

      {expanded && <>
        {/* Audience + Tag row */}
        <div style={{ padding: '0 20px 12px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Audience selector */}
          <div style={{ display: 'flex', background: 'var(--card-inner)', borderRadius: 10, border: '1px solid var(--border)', overflow: 'hidden' }}>
            {[['everyone', 'Everyone', ICONS.globe], ['department', 'My Dept', ICONS.users]].map(([val, label, icon]) => (
              <button key={val} onClick={() => setAudience(val)} style={{
                display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px',
                border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                background: audience === val ? 'var(--accent)' : 'transparent',
                color: audience === val ? 'white' : 'var(--muted)', transition: 'all 0.15s',
              }}>
                <Icon d={icon} size={12} stroke="currentColor" />{label}
              </button>
            ))}
          </div>

          {/* Tag users */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => setShowTagPicker(s => !s)} style={{
              display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px',
              borderRadius: 10, border: '1px solid var(--border)', background: tagged.length > 0 ? 'rgba(13,148,136,0.1)' : 'transparent',
              color: tagged.length > 0 ? 'var(--accent)' : 'var(--muted)', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>
              <Icon d={ICONS.tag} size={12} />
              {tagged.length > 0 ? `${tagged.length} tagged` : 'Tag people'}
            </button>
            {showTagPicker && (
              <div style={{ position: 'absolute', top: 36, left: 0, zIndex: 50, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, boxShadow: '0 8px 32px rgba(0,0,0,0.15)', width: 220, overflow: 'hidden' }}>
                <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
                  <input autoFocus value={tagSearch} onChange={e => setTagSearch(e.target.value)} placeholder="Search people..."
                    style={{ width: '100%', border: 'none', background: 'var(--card-inner)', borderRadius: 8, padding: '6px 10px', fontSize: 13, color: 'var(--text)', outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                  {filteredUsers.slice(0, 8).map(u => (
                    <div key={u.id} onClick={() => { setTagged(p => [...p, u]); setTagSearch(''); }} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer', transition: 'background 0.1s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--card-inner)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <Avatar name={u.full_name} url={u.avatar_url} size={28} />
                      <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>{u.full_name}</span>
                    </div>
                  ))}
                  {filteredUsers.length === 0 && <div style={{ padding: '12px', fontSize: 13, color: 'var(--muted)', textAlign: 'center' }}>No users found</div>}
                </div>
              </div>
            )}
          </div>

          {/* Tagged users pills */}
          {tagged.map(u => (
            <span key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 20, background: 'rgba(13,148,136,0.12)', color: 'var(--accent)', fontSize: 12, fontWeight: 600 }}>
              @{u.full_name.split(' ')[0]}
              <button onClick={() => setTagged(p => p.filter(t => t.id !== u.id))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', display: 'flex', padding: 0, marginLeft: 2 }}>
                <Icon d={ICONS.close} size={11} />
              </button>
            </span>
          ))}
        </div>

        {/* File preview */}
        {file && (
          <div style={{ margin: '0 20px 12px', borderRadius: 14, overflow: 'hidden', border: '1px solid var(--border)', position: 'relative' }}>
            {fileType === 'image' && filePreview && <img src={filePreview} style={{ width: '100%', maxHeight: 240, objectFit: 'cover', display: 'block' }} alt="" />}
            {fileType === 'video' && filePreview && <video src={filePreview} style={{ width: '100%', maxHeight: 240, display: 'block' }} controls />}
            {fileType === 'document' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: 'var(--card-inner)' }}>
                <div style={{ width: 44, height: 52, borderRadius: 10, background: 'linear-gradient(135deg,#dc2626,#991b1b)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: 'white', fontSize: 9, fontWeight: 800 }}>FILE</span>
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{file.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{(file.size / 1024 / 1024).toFixed(1)} MB</div>
                </div>
              </div>
            )}
            <button onClick={removeFile} style={{ position: 'absolute', top: 8, right: 8, width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', border: 'none', cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon d={ICONS.close} size={14} />
            </button>
          </div>
        )}

        {/* Poll builder */}
        {showPoll && (
          <div style={{ margin: '0 20px 12px', padding: '14px 16px', background: 'var(--card-inner)', borderRadius: 14, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 10 }}>Poll Options</div>
            {pollOptions.map((opt, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', border: '2px solid var(--border)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'var(--muted)', fontWeight: 700 }}>{i + 1}</div>
                <input value={opt} onChange={e => updatePollOption(i, e.target.value)} placeholder={`Option ${i + 1}`}
                  style={{ flex: 1, padding: '8px 12px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13, outline: 'none' }} />
                {pollOptions.length > 2 && (
                  <button onClick={() => removePollOption(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', display: 'flex' }}>
                    <Icon d={ICONS.trash} size={15} />
                  </button>
                )}
              </div>
            ))}
            {pollOptions.length < 6 && (
              <button onClick={addPollOption} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 10, border: '1px dashed var(--border)', background: 'transparent', color: 'var(--accent)', fontSize: 12, fontWeight: 600, cursor: 'pointer', width: '100%', justifyContent: 'center', marginTop: 4 }}>
                <Icon d={ICONS.plus} size={13} /> Add Option
              </button>
            )}
          </div>
        )}

        {/* Action bar */}
        <div style={{ padding: '0 20px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={e => handleFile(e, fileType)} />
            {[
              { icon: ICONS.image, label: 'Photo', color: '#16a34a', type: 'image', accept: 'image/*' },
              { icon: ICONS.video, label: 'Video', color: '#2563eb', type: 'video', accept: 'video/*' },
              { icon: ICONS.file, label: 'Document', color: '#dc2626', type: 'document', accept: '.pdf,.doc,.docx,.xls,.xlsx' },
            ].map(btn => (
              <button key={btn.label} onClick={() => { setFileType(btn.type); fileRef.current.accept = btn.accept; fileRef.current.click(); setShowPoll(false); }}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 10px', borderRadius: 10, border: '1px solid var(--border)', background: file && fileType === btn.type ? btn.color + '18' : 'transparent', color: btn.color, fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = btn.color + '12'}
                onMouseLeave={e => e.currentTarget.style.background = file && fileType === btn.type ? btn.color + '18' : 'transparent'}>
                <Icon d={btn.icon} size={13} stroke={btn.color} />{btn.label}
              </button>
            ))}

            <button onClick={() => { setShowPoll(s => !s); removeFile(); }}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 10px', borderRadius: 10, border: '1px solid var(--border)', background: showPoll ? 'rgba(124,58,237,0.12)' : 'transparent', color: '#7c3aed', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              <Icon d={ICONS.poll} size={13} stroke="#7c3aed" />Poll
            </button>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => { setExpanded(false); setText(''); removeFile(); setTagged([]); setShowPoll(false); }}
              style={{ padding: '8px 16px', borderRadius: 12, border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
            <button onClick={handlePost} disabled={!canPost || posting}
              style={{ padding: '8px 20px', borderRadius: 12, border: 'none', cursor: canPost && !posting ? 'pointer' : 'not-allowed', background: canPost ? 'linear-gradient(135deg,#0d9488,#2563eb)' : 'var(--border)', color: canPost ? 'white' : 'var(--muted)', fontSize: 13, fontWeight: 700, transition: 'all 0.2s' }}>
              {posting ? 'Posting...' : 'Post'}
            </button>
          </div>
        </div>
      </>}

      {/* Collapsed action row */}
      {!expanded && (
        <div style={{ padding: '0 20px 14px 76px', display: 'flex', gap: 6 }}>
          {[
            { icon: ICONS.image, label: 'Photo', color: '#16a34a' },
            { icon: ICONS.video, label: 'Video', color: '#2563eb' },
            { icon: ICONS.file, label: 'Document', color: '#dc2626' },
            { icon: ICONS.poll, label: 'Poll', color: '#7c3aed' },
          ].map(btn => (
            <button key={btn.label} onClick={() => setExpanded(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 10, border: '1px solid var(--border)', background: 'transparent', color: btn.color, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              <Icon d={btn.icon} size={12} stroke={btn.color} />{btn.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Post Card ─────────────────────────────────────────────────────────────────
const PollWidget = ({ post, currentUser, onVote }) => {
  const [votes, setVotes] = useState(post.poll_votes || {});
  const [voters, setVoters] = useState({});
  const [voterDetails, setVoterDetails] = useState({});
  const [showVoters, setShowVoters] = useState(null);
  const [loading, setLoading] = useState(false);
  const myVote = votes[currentUser?.id];
  const total = Object.keys(votes).length;

  const handleVote = async (i) => {
    const newVotes = { ...votes };
    if (newVotes[currentUser?.id] === i) delete newVotes[currentUser?.id];
    else newVotes[currentUser?.id] = i;
    setVotes(newVotes);
    await onVote(post.id, i);
  };

  const loadVoters = async (i) => {
    if (showVoters === i) { setShowVoters(null); return; }
    setLoading(true);
    setShowVoters(i);
    try {
      const { data } = await axios.get(`${API_URL}/announcements/${post.id}/poll-voters`, { headers: getAuthHeader() });
      setVoters(data.voters || {});
      setVoterDetails(data.voter_details || {});
    } catch(e) {}
    setLoading(false);
  };

  return (
    <div style={{ margin: '0 20px 12px', padding: '14px 16px', background: 'var(--card-inner)', borderRadius: 14, border: '1px solid var(--border)' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 12 }}>📊 Poll</div>
      {post.poll_options.map((opt, i) => {
        const optVotes = Object.values(votes).filter(v => v === i).length;
        const pct = total > 0 ? Math.round(optVotes / total * 100) : 0;
        const isMyVote = myVote === i;
        return (
          <div key={i} style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, alignItems: 'center' }}>
              <button onClick={() => handleVote(i)} style={{
                display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none',
                cursor: 'pointer', padding: 0, textAlign: 'left',
              }}>
                <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${isMyVote ? '#0d9488' : 'var(--border)'}`, background: isMyVote ? '#0d9488' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s' }}>
                  {isMyVote && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'white' }} />}
                </div>
                <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: isMyVote ? 600 : 400 }}>{opt}</span>
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>{pct}%</span>
                <button onClick={() => loadVoters(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--accent)', fontWeight: 600, padding: 0 }}>
                  {optVotes} {optVotes === 1 ? 'vote' : 'votes'}
                </button>
              </div>
            </div>
            <div style={{ height: 8, background: 'var(--border)', borderRadius: 99, overflow: 'hidden', cursor: 'pointer' }} onClick={() => handleVote(i)}>
              <div style={{ height: '100%', width: pct + '%', borderRadius: 99, background: isMyVote ? 'linear-gradient(90deg,#0d9488,#2563eb)' : 'rgba(13,148,136,0.3)', transition: 'width 0.5s' }} />
            </div>
            {showVoters === i && (
              <div onClick={() => setShowVoters(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div onClick={e => e.stopPropagation()} style={{ background: 'var(--card)', borderRadius: 20, width: 340, maxHeight: 480, display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.4)', border: '1px solid var(--border)' }}>
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>Votes for "{post.poll_options[i]}"</div>
                      <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{voters[i]?.length || 0} {voters[i]?.length === 1 ? 'person' : 'people'} voted</div>
                    </div>
                    <button onClick={() => setShowVoters(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 24, lineHeight: 1, padding: 4 }}>×</button>
                  </div>
                  <div style={{ overflowY: 'auto', maxHeight: 360, padding: '8px 0' }}>
                    {loading ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32, gap: 10 }}>
                        <div style={{ width: 20, height: 20, border: '3px solid var(--border)', borderTop: '3px solid #0d9488', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                        <span style={{ color: 'var(--muted)', fontSize: 13 }}>Loading voters...</span>
                      </div>
                    ) : voters[i] && voters[i].length > 0 ? voters[i].map((name, j) => {
                      const detail = voterDetails[name] || {};
                      const colors = ['#0d9488','#2563eb','#7c3aed','#db2777','#ea580c','#16a34a'];
                      const color = colors[name.charCodeAt(0) % colors.length];
                      const avatarUrl = detail.avatar_url;
                      return (
                        <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', borderBottom: '1px solid var(--border)', transition: 'background 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.background='var(--card-inner)'}
                          onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                          <div style={{ width: 40, height: 40, borderRadius: '50%', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, color: 'white', flexShrink: 0, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                            {avatarUrl
                              ? <img src={avatarUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" onError={e => { e.target.style.display='none'; }}/>
                              : name[0]?.toUpperCase()}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{name}</div>
                            {detail.designation && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>{detail.designation}</div>}
                          </div>
                          <div style={{ fontSize: 11, color: '#0d9488', fontWeight: 600 }}>✓ Voted</div>
                        </div>
                      );
                    }) : (
                      <div style={{ textAlign: 'center', padding: 32 }}>
                        <div style={{ fontSize: 32, marginBottom: 8 }}>🗳️</div>
                        <div style={{ color: 'var(--muted)', fontSize: 13 }}>No votes yet</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
      <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
        {total} {total === 1 ? 'vote' : 'votes'} · {myVote !== undefined ? 'You voted' : 'Click to vote'}
      </div>
    </div>
  );
};

const PostCard = ({ post, currentUser, onPin, onDelete, users = [] }) => {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [liked, setLiked] = useState(post.liked);
  const [likeCount, setLikeCount] = useState(parseInt(post.likes) || 0);
  const [reactors, setReactors] = useState(null);
  const openReactors = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/announcements/${post.id}/reactions`, { headers: getAuthHeader() });
      setReactors(Array.isArray(data) ? data : []);
    } catch(e) { setReactors([]); }
  };
  const [commentMentions, setCommentMentions] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null); // null = not mentioning; string = active query
  const mentionMatches = mentionQuery !== null
    ? (users || []).filter(u => u.id !== currentUser?.id && (u.full_name || '').toLowerCase().includes(mentionQuery.toLowerCase())).slice(0, 6)
    : [];
  const onCommentChange = (val) => {
    setCommentText(val);
    // Detect an active @token at the end of the input (no space after @)
    const m = val.match(/@([^\s@]*)$/);
    setMentionQuery(m ? m[1] : null);
  };
  const pickMention = (u) => {
    // Replace the trailing @token with @FullName + space
    setCommentText(prev => prev.replace(/@([^\s@]*)$/, `@${u.full_name} `));
    setCommentMentions(prev => prev.find(x => x.id === u.id) ? prev : [...prev, u]);
    setMentionQuery(null);
  };
  const [profileUser, setProfileUser] = useState(null);
  const openProfile = async (userId) => {
    if (!userId) return;
    setProfileUser({ loading: true });
    try {
      const { data } = await axios.get(`${API_URL}/users/${userId}`, { headers: getAuthHeader() });
      setProfileUser(data);
    } catch(e) { setProfileUser(null); }
  };
  const [commentText, setCommentText] = useState('');
  const [taggedUsers, setTaggedUsers] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [showCommentEmoji, setShowCommentEmoji] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(post.content);
  const [showMenu, setShowMenu] = useState(false);
  const isOwner = currentUser?.id === post.user_id;

  useEffect(() => {
    if (post.tagged_user_ids?.length > 0) {
      axios.get(`${API_URL}/announcements/${post.id}/tagged`, { headers: getAuthHeader() })
        .then(r => setTaggedUsers(r.data)).catch(() => {});
    }
  }, [post.id, post.tagged_user_ids]);

  const handleDelete = async () => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(`${API_URL}/announcements/${post.id}`, { headers: getAuthHeader() });
      toast.success('Post deleted'); onDelete(post.id);
    } catch(e) { toast.error('Failed to delete'); }
  };

  const handleEdit = async () => {
    try {
      await axios.put(`${API_URL}/announcements/${post.id}`, { content: editText }, { headers: getAuthHeader() });
      toast.success('Updated'); setEditing(false);
    } catch(e) { toast.error('Failed to update'); }
  };

  const loadComments = async () => {
    if (showComments) { setShowComments(false); return; }
    try {
      const { data } = await axios.get(`${API_URL}/announcements/${post.id}/comments`, { headers: getAuthHeader() });
      setComments(data);
    } catch(e) {}
    setShowComments(true);
  };

  const handleLike = async () => {
    // Optimistic flip based on current state
    const wasLiked = liked;
    setLiked(!wasLiked); setLikeCount(c => wasLiked ? c - 1 : c + 1);
    try {
      const { data } = await axios.post(`${API_URL}/announcements/${post.id}/react`, { emoji: '❤️' }, { headers: getAuthHeader() });
      // Reconcile with server's authoritative state (prevents desync / double-react)
      if (typeof data?.liked === 'boolean' && data.liked !== !wasLiked) {
        setLiked(data.liked);
        setLikeCount(c => data.liked ? c + 1 : c - 1);
      }
    }
    catch(e) { setLiked(wasLiked); setLikeCount(c => wasLiked ? c + 1 : c - 1); }
  };

  const renderWithLinks = (text) => {
    if (!text) return text;
    const parts = String(text).split(/(https?:\/\/[^\s]+)/g);
    return parts.map((p, i) =>
      /^https?:\/\//.test(p)
        ? <a key={'l'+i} href={p} target="_blank" rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            style={{ color: '#2563eb', textDecoration: 'underline', wordBreak: 'break-all' }}>{p}</a>
        : p
    );
  };
  const renderCommentContent = (c) => {
    const mentions = c.mentioned_users || [];
    if (!mentions.length || !c.content) return renderWithLinks(c.content);
    // Split content on each @FullName and render those as clickable
    let parts = [c.content];
    mentions.forEach(mu => {
      const token = `@${mu.full_name}`;
      parts = parts.flatMap(part => {
        if (typeof part !== 'string' || !part.includes(token)) return [part];
        const segs = part.split(token);
        const out = [];
        segs.forEach((s, i) => {
          out.push(s);
          if (i < segs.length - 1) out.push({ mention: mu });
        });
        return out;
      });
    });
    return parts.map((p, i) => typeof p === 'string'
      ? p
      : <span key={i} onClick={() => openProfile(p.mention.id)} style={{ color: '#2563eb', fontWeight: 600, cursor: 'pointer' }}>@{p.mention.full_name}</span>);
  };
  const handleComment = async () => {
    if (!commentText.trim()) return;
    try {
      const mentionIds = commentMentions.filter(m => commentText.includes(`@${m.full_name}`)).map(m => m.id);
      const { data } = await axios.post(`${API_URL}/announcements/${post.id}/comments`,
        { content: commentText, reply_to_id: replyTo?.id || null, mentioned_user_ids: mentionIds },
        { headers: getAuthHeader() });
      const mentionedUsers = commentMentions.filter(m => mentionIds.includes(m.id)).map(m => ({ id: m.id, full_name: m.full_name }));
      setComments(prev => [...prev, { id: data.id, content: commentText, created_at: data.created_at, full_name: currentUser?.full_name, avatar_url: currentUser?.avatar_url, reply_to_id: replyTo?.id || null, mentioned_users: mentionedUsers }]);
      setCommentText(''); setReplyTo(null); setCommentMentions([]); setMentionQuery(null);
    } catch(e) { toast.error('Failed to comment'); }
  };

  const mediaUrl = post.media_url ? (post.media_url.startsWith('http') ? post.media_url : `https://qtalk.qualtechedge.in${post.media_url}`) : null;

  return (
    <div style={{ background: 'var(--card)', borderRadius: 20, border: '1px solid var(--border)', overflow: 'hidden', marginBottom: 16, boxShadow: '0 2px 20px rgba(0,0,0,0.04)' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px 12px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <Avatar name={post.full_name} url={post.avatar_url} size={44} />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>{post.full_name}</span>
            {post.designation && <span style={{ fontSize: 12, color: 'var(--muted)' }}>{post.designation}</span>}
            {post.department && <span style={{ background: '#0d948818', color: '#0d9488', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20 }}>{post.department}</span>}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 2 }}>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>{timeAgo(post.created_at)}</span>
            {post.audience === 'department' && (
              <span style={{ fontSize: 11, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 3 }}>
                <Icon d={ICONS.users} size={11} /> Dept only
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', position: 'relative' }}>
          {post.is_pinned && <span style={{ color: '#f59e0b', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}><Icon d={ICONS.pin} size={12} />Pinned</span>}
          <button onClick={() => setShowMenu(s => !s)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', padding: '4px 6px', borderRadius: 8, display:'flex', alignItems:'center' }}>
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>
          </button>
          {showMenu && (
            <div onMouseLeave={() => setShowMenu(false)} style={{ position: 'absolute', right: 0, top: 28, zIndex: 50, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, boxShadow: '0 8px 32px rgba(0,0,0,0.15)', minWidth: 160, overflow: 'hidden' }}>
              {currentUser?.is_admin && <button onClick={() => { onPin(post.id); setShowMenu(false); }} style={{ width: '100%', textAlign: 'left', padding: '10px 16px', border: 'none', background: 'transparent', color: 'var(--text)', fontSize: 13, cursor: 'pointer' }}>{post.is_pinned ? '📌 Unpin' : '📌 Pin post'}</button>}
              {isOwner && <button onClick={() => { setEditing(true); setShowMenu(false); }} style={{ width: '100%', textAlign: 'left', padding: '10px 16px', border: 'none', background: 'transparent', color: 'var(--text)', fontSize: 13, cursor: 'pointer' }}>✏️ Edit post</button>}
              {(isOwner || currentUser?.is_admin) && <button onClick={() => { handleDelete(); setShowMenu(false); }} style={{ width: '100%', textAlign: 'left', padding: '10px 16px', border: 'none', background: 'transparent', color: '#ef4444', fontSize: 13, cursor: 'pointer' }}>🗑️ Delete post</button>}
              <button onClick={() => { navigator.clipboard.writeText(window.location.origin + '/announcements#' + post.id); toast.success('Link copied!'); setShowMenu(false); }} style={{ width: '100%', textAlign: 'left', padding: '10px 16px', border: 'none', background: 'transparent', color: 'var(--text)', fontSize: 13, cursor: 'pointer' }}>🔗 Copy link</button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '0 20px 12px' }}>
        {editing ? (
          <div>
            <textarea value={editText} onChange={e => setEditText(e.target.value)}
              style={{ width: '100%', border: '1px solid var(--border)', background: 'var(--card-inner)', borderRadius: 12, padding: '10px 14px', fontSize: 14, color: 'var(--text)', resize: 'none', outline: 'none', fontFamily: 'inherit', lineHeight: 1.6, boxSizing: 'border-box', minHeight: 80 }} rows={3} />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button onClick={() => setEditing(false)} style={{ padding: '6px 14px', borderRadius: 10, border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleEdit} style={{ padding: '6px 14px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Save</button>
            </div>
          </div>
        ) : (
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{renderWithLinks(editText)}</p>
        )}
        {taggedUsers.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>Tagged:</span>
            {taggedUsers.map(u => (
              <span key={u.id} onClick={() => openProfile(u.id)} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#2563eb', fontWeight: 600, background: 'rgba(37,99,235,0.1)', padding: '2px 8px', borderRadius: 12, cursor: 'pointer' }}>
                @{u.full_name.split(' ')[0]}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Media */}
      {post.poll_options && post.poll_options.length > 0 && (
        <PollWidget post={post} currentUser={currentUser} onVote={async (postId, optIdx) => {
          try {
            await axios.post(`${API_URL}/announcements/${postId}/vote`, { option_index: optIdx }, { headers: getAuthHeader() });
          } catch(e) {}
        }} />
      )}

      {mediaUrl && post.media_type === 'image' && (
        <>
          <div style={{ margin: '0 20px 12px', borderRadius: 14, overflow: 'hidden', cursor: 'zoom-in' }}
            onClick={() => setLightboxImg(mediaUrl)}>
            <img src={mediaUrl} style={{ width: '100%', maxHeight: 400, objectFit: 'cover', display: 'block' }} alt="" />
          </div>
          {lightboxImg && (
            <div onClick={() => setLightboxImg(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
              <div style={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 10 }}>
                <button onClick={async e => { e.stopPropagation(); try { const token=localStorage.getItem('qtalk_token'); const proxyUrl=`https://qtalk.qualtechedge.in/api/files/proxy-download?url=${encodeURIComponent(lightboxImg)}&filename=image.jpg`; const r=await fetch(proxyUrl,{headers:{Authorization:'Bearer '+token}}); const b=await r.blob(); const u=URL.createObjectURL(b); const a=document.createElement('a'); a.href=u; a.download='announcement-image.jpg'; document.body.appendChild(a); a.click(); document.body.removeChild(a); setTimeout(()=>URL.revokeObjectURL(u),1000); } catch(err) { console.error(err); } }}
                  style={{ background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 10, padding: '8px 14px', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:15,height:15}}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  Download
                </button>
                <button onClick={e => { e.stopPropagation(); setLightboxImg(null); }}
                  style={{ background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 10, padding: '8px 14px', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:15,height:15}}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  Close
                </button>
              </div>
              <img src={lightboxImg} onClick={e => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }} alt="" />
            </div>
          )}
        </>
      )}
      {mediaUrl && post.media_type === 'video' && (
        <div style={{ margin: '0 20px 12px', borderRadius: 14, overflow: 'hidden' }}>
          <video src={mediaUrl} controls style={{ width: '100%', maxHeight: 360, display: 'block' }} />
        </div>
      )}
      {mediaUrl && post.media_type === 'document' && (
        <div style={{ margin: '0 20px 12px', borderRadius: 14, padding: '14px 16px', background: 'var(--card-inner)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 44, height: 52, borderRadius: 10, background: 'linear-gradient(135deg,#dc2626,#991b1b)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ color: 'white', fontSize: 9, fontWeight: 800 }}>FILE</span>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Attached Document</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>Click to download</div>
          </div>
          <a href={mediaUrl} download target="_blank" rel="noreferrer" style={{ padding: '7px 16px', borderRadius: 10, border: '1px solid var(--border)', background: 'transparent', color: 'var(--accent)', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>Download</a>
        </div>
      )}

      {/* Stats */}
      <div style={{ padding: '0 20px 10px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 12, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
          <Icon d={ICONS.eye} size={13} />{(parseInt(post.view_count) || 0) + (post.viewed_by_me ? 0 : 1)} views
        </span>
        {likeCount > 0 && (
          <span onClick={openReactors} style={{ fontSize: 12, color: 'var(--muted)', cursor: 'pointer' }}>❤️ {likeCount}</span>
        )}
      </div>
      {reactors !== null && (
        <div onClick={() => setReactors(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: 'var(--card)', borderRadius: 16, width: 340, maxHeight: '70vh', overflow: 'hidden', boxShadow: '0 12px 48px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>Reactions</span>
              <span onClick={() => setReactors(null)} style={{ cursor: 'pointer', color: 'var(--muted)', fontSize: 18 }}>×</span>
            </div>
            <div style={{ overflowY: 'auto', maxHeight: 'calc(70vh - 52px)' }}>
              {reactors.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>No reactions yet</div>
              ) : reactors.map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 18px', borderBottom: i < reactors.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ width: 38, height: 38, borderRadius: 19, background: '#0d9488', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                    {r.avatar_url ? <img src={r.avatar_url.startsWith('http') ? r.avatar_url : `https://qtalk.qualtechedge.in${r.avatar_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ color: '#fff', fontWeight: 700 }}>{(r.full_name || '?').charAt(0).toUpperCase()}</span>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{r.full_name}</div>
                    {r.designation && <div style={{ fontSize: 11, color: 'var(--muted)' }}>{r.designation}</div>}
                  </div>
                  <span style={{ fontSize: 18 }}>{r.emoji}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {profileUser && (
        <div onClick={() => setProfileUser(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: 'var(--card)', borderRadius: 16, width: 320, overflow: 'hidden', boxShadow: '0 12px 48px rgba(0,0,0,0.3)' }}>
            {profileUser.loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>Loading...</div>
            ) : (
              <>
                <div style={{ background: 'linear-gradient(135deg,#0d9488,#2563eb)', padding: '24px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ width: 72, height: 72, borderRadius: 36, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', marginBottom: 10, border: '3px solid rgba(255,255,255,0.4)' }}>
                    {profileUser.avatar_url ? <img src={profileUser.avatar_url.startsWith('http') ? profileUser.avatar_url : `https://qtalk.qualtechedge.in${profileUser.avatar_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ color: '#fff', fontWeight: 700, fontSize: 28 }}>{(profileUser.full_name || '?').charAt(0).toUpperCase()}</span>}
                  </div>
                  <div style={{ color: '#fff', fontSize: 17, fontWeight: 700 }}>{profileUser.full_name}</div>
                  {profileUser.designation && <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12, marginTop: 2 }}>{profileUser.designation}</div>}
                </div>
                <div style={{ padding: '14px 18px' }}>
                  {[['Department', profileUser.department], ['Email', profileUser.email], ['Mobile', profileUser.mobile]].map(([label, val]) => val ? (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                      <span style={{ color: 'var(--muted)' }}>{label}</span>
                      <span style={{ color: 'var(--text)', fontWeight: 500 }}>{val}</span>
                    </div>
                  ) : null)}
                  <a href={`/chat`} style={{ display: 'block', textAlign: 'center', marginTop: 14, padding: '9px', background: 'linear-gradient(135deg,#0d9488,#2563eb)', color: '#fff', borderRadius: 10, textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>💬 Message</a>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Action bar */}
      <div style={{ borderTop: '1px solid var(--border)', display: 'flex' }}>
        {[
          { icon: ICONS.heart, label: `${likeCount} Like`, action: handleLike, active: liked, color: '#ef4444', fill: liked ? '#ef4444' : 'none' },
          { icon: ICONS.comment, label: `${post.comment_count || 0} Comment`, action: loadComments, active: showComments, color: 'var(--accent)', fill: 'none' },
          { icon: ICONS.share, label: 'Share', action: () => { navigator.clipboard.writeText(window.location.href); toast.success('Link copied!'); }, active: false, color: 'var(--accent)', fill: 'none' },
        ].map((btn, i) => (
          <button key={i} onClick={btn.action} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '11px 4px', background: 'transparent', border: 'none', cursor: 'pointer', color: btn.active ? btn.color : 'var(--muted)', fontSize: 13, fontWeight: btn.active ? 600 : 400, transition: 'all 0.15s', borderRight: i < 2 ? '1px solid var(--border)' : 'none' }}>
            <Icon d={btn.icon} size={15} fill={btn.fill} stroke={btn.active ? btn.color : 'currentColor'} />
            {btn.label}
          </button>
        ))}
      </div>

      {/* Comments */}
      {showComments && (
        <div style={{ padding: '12px 20px 16px' }}>
          {comments.map((c, i) => (
            <div key={c.id || i} style={{ display: 'flex', gap: 10, marginBottom: 10, paddingLeft: c.reply_to_id ? 36 : 0 }}>
              <Avatar name={c.full_name} url={c.avatar_url} size={28} />
              <div style={{ flex: 1 }}>
                <div style={{ background: 'var(--card-inner)', borderRadius: 12, padding: '8px 12px' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', marginRight: 8 }}>{c.full_name}</span>
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{renderCommentContent(c)}</span>
                </div>
                <div style={{ display: 'flex', gap: 10, paddingLeft: 12, marginTop: 3 }}>
                  <span style={{ fontSize: 11, color: 'var(--muted)' }}>{timeAgo(c.created_at)}</span>
                  <button onClick={async () => {
                    try {
                      await axios.post(`${API_URL}/announcements/comments/${c.id}/react`, { emoji: '❤️' }, { headers: getAuthHeader() });
                      setComments(prev => prev.map(x => x.id === c.id ? { ...x, liked: !x.liked, like_count: (x.like_count || 0) + (x.liked ? -1 : 1) } : x));
                    } catch(e) {}
                  }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: c.liked ? '#ef4444' : 'var(--muted)', padding: 0 }}>
                    ❤️ {c.like_count || ''}
                  </button>
                  <button onClick={() => setReplyTo(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: 'var(--accent)', padding: 0 }}>Reply</button>
                </div>
              </div>
            </div>
          ))}
          {replyTo && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: 'rgba(13,148,136,0.08)', borderRadius: 10, marginBottom: 8, border: '1px solid rgba(13,148,136,0.2)' }}>
              <span style={{ fontSize: 12, color: 'var(--accent)', flex: 1 }}>↩ Replying to <strong>{replyTo.full_name}</strong></span>
              <button onClick={() => setReplyTo(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', display: 'flex' }}><Icon d={ICONS.close} size={13} /></button>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8, position: 'relative' }}>
            {mentionQuery !== null && mentionMatches.length > 0 && (
              <div style={{ position: 'absolute', bottom: 44, left: 38, right: 0, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', zIndex: 100, overflow: 'hidden', maxHeight: 200, overflowY: 'auto' }}>
                {mentionMatches.map(u => (
                  <div key={u.id} onClick={() => pickMention(u)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}>
                    <Avatar name={u.full_name} url={u.avatar_url} size={24} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{u.full_name}</div>
                      {u.designation && <div style={{ fontSize: 11, color: 'var(--muted)' }}>{u.designation}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Avatar name={currentUser?.full_name} url={currentUser?.avatar_url} size={28} />
            <div style={{ flex: 1, display: 'flex', gap: 8, background: 'var(--card-inner)', borderRadius: 20, padding: '6px 14px', alignItems: 'center', border: '1px solid var(--border)' }}>
              <input value={commentText} onChange={e => onCommentChange(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && mentionQuery === null) handleComment(); }}
                placeholder={replyTo ? `Reply to ${replyTo.full_name}...` : 'Write a comment... (@ to mention)'}
                style={{ flex: 1, border: 'none', background: 'transparent', color: 'var(--text)', fontSize: 13, outline: 'none' }} />
              <div style={{ position: 'relative' }}>
                <button onClick={() => setShowCommentEmoji(s => !s)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#f59e0b', display: 'flex' }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:16,height:16}}><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
                </button>
                {showCommentEmoji && (
                  <div style={{ position: 'absolute', bottom: 30, right: 0, zIndex: 999, boxShadow: '0 8px 32px rgba(0,0,0,0.2)', borderRadius: 16, overflow: 'hidden' }}>
                    <EmojiPicker onEmojiClick={e => { setCommentText(prev => prev + e.emoji); setShowCommentEmoji(false); }} height={300} width={280} skinTonesDisabled previewConfig={{showPreview:false}} />
                  </div>
                )}
              </div>
              <button onClick={handleComment} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', display: 'flex', padding: 0 }}>
                <Icon d={ICONS.send} size={15} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Sidebar ───────────────────────────────────────────────────────────────────
const NewsWidget = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('finance');
  const [visibleCount, setVisibleCount] = useState(6);
  const [loadingMore, setLoadingMore] = useState(false);
  const handleLoadMore = () => {
    setLoadingMore(true);
    setTimeout(() => { setVisibleCount(v => v + 6); setLoadingMore(false); }, 400);
  };

  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem('qtalk_token');
    fetch(`https://qtalk.qualtechedge.in/api/announcements/news-feed?tab=${tab}&t=${Date.now()}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.text())
      .then(xmlText => {
        const parser = new DOMParser();
        const xml = parser.parseFromString(xmlText, 'text/xml');
        const items = Array.from(xml.querySelectorAll('item')).map(item => ({
          title: item.querySelector('title')?.textContent?.replace(/ - [^-]*$/, '') || '',
          link: item.querySelector('link')?.textContent || '',
          pubDate: item.querySelector('pubDate')?.textContent || '',
          source: item.querySelector('source')?.textContent || 'Google News'
        }))
        .sort((a, b) => new Date(b.pubDate) - new Date(a.pubDate));
        setNews(items);
        setVisibleCount(6);
        setLoading(false);
      })
      .catch(() => { setNews([]); setLoading(false); });
  }, [tab]);

  return (
    <div style={{ background: 'var(--card)', borderRadius: 20, border: '1px solid var(--border)', padding: '16px 18px' }}>
      <style>{`@keyframes qspin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: '#0d9488' }}><Icon d={ICONS.trending} size={16} /></span>
          <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>Industry News</span>
        </div>
        <div style={{ display: 'flex', background: 'var(--card-inner)', borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden' }}>
          {[['finance', 'Finance'], ['tech', 'Tech']].map(([val, label]) => (
            <button key={val} onClick={() => setTab(val)} style={{
              padding: '4px 10px', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600,
              background: tab === val ? 'var(--accent)' : 'transparent',
              color: tab === val ? 'white' : 'var(--muted)', transition: 'all 0.15s',
            }}>{label}</button>
          ))}
        </div>
      </div>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--muted)', fontSize: 13 }}>Loading news...</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {news.slice(0, visibleCount).map((item, i) => {
            const newsTimeAgo = (d) => {
              if (!d) return '';
              const diff = (Date.now() - new Date(d).getTime()) / 1000;
              if (diff < 3600) return Math.floor(diff/60) + 'm ago';
              if (diff < 86400) return Math.floor(diff/3600) + 'h ago';
              return Math.floor(diff/86400) + 'd ago';
            };
            const cleanTitle = item.title?.replace(/ - [^-]+$/, '').substring(0, 80);
            const source = item.author?.replace(/www\./,'').split('.')[0] || 'News';
            return (
              <a key={i} href={item.link} target="_blank" rel="noreferrer" style={{
                display: 'block', padding: '10px 0', textDecoration: 'none',
                borderBottom: i < Math.min(visibleCount, news.length) - 1 ? '1px solid var(--border)' : 'none',
              }}
                onMouseEnter={e => { e.currentTarget.querySelector('.news-title').style.color='var(--accent)'; }}
                onMouseLeave={e => { e.currentTarget.querySelector('.news-title').style.color='var(--text)'; }}>
                <div className="news-title" style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', lineHeight: 1.45, marginBottom: 5, transition: 'color 0.15s' }}>
                  {cleanTitle}{item.title?.length > 80 ? '...' : ''}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 6, background: 'var(--card-inner)', border: '1px solid var(--border)', color: 'var(--muted)', fontWeight: 600, textTransform: 'capitalize' }}>
                    {source}
                  </span>
                  <span style={{ fontSize: 10, color: 'var(--muted)' }}>·</span>
                  <span style={{ fontSize: 10, color: 'var(--muted)' }}>{newsTimeAgo(item.pubDate)}</span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:9,height:9,color:'var(--muted)',marginLeft:'auto',flexShrink:0}}><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                </div>
              </a>
            );
          })}
        </div>
      )}
      {!loading && visibleCount < news.length && (
        <button onClick={handleLoadMore} disabled={loadingMore} style={{
          width: '100%', marginTop: 10, padding: '8px', borderRadius: 10, border: '1px solid var(--border)',
          background: 'var(--card-inner)', color: 'var(--accent)', fontSize: 12, fontWeight: 600,
          cursor: loadingMore ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
        }}>
          {loadingMore ? (
            <>
              <span style={{ width: 13, height: 13, border: '2px solid var(--accent)', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'qspin 0.6s linear infinite' }} />
              Loading...
            </>
          ) : 'Load more news'}
        </button>
      )}
    </div>
  );
};

const Sidebar = ({ pinned }) => {
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  useEffect(() => {
    axios.get(`${API_URL}/meetings`, { headers: getAuthHeader() })
      .then(({ data }) => {
        const now = new Date();
        const upcoming = (data || [])
          .filter(m => new Date(m.scheduled_at) > now && m.my_status !== 'declined')
          .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
          .slice(0, 4);
        setUpcomingMeetings(upcoming);
      })
      .catch(() => {});
  }, []);
  return (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
    <div style={{ background: 'var(--card)', borderRadius: 20, border: '1px solid var(--border)', padding: '16px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <span style={{ color: '#f59e0b' }}><Icon d={ICONS.pin} size={16} /></span>
        <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>Pinned Announcements</span>
      </div>
      {pinned.length === 0 && <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0 }}>No pinned announcements yet. Use ⋯ menu on any post to pin it.</p>}
      {pinned.slice(0, 3).map((p, i) => (
        <div key={p.id} style={{ padding: '10px 0', borderBottom: i < Math.min(pinned.length, 3) - 1 ? '1px solid var(--border)' : 'none' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', lineHeight: 1.4 }}>{p.content?.substring(0, 60)}{p.content?.length > 60 ? '...' : ''}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>{p.full_name} · {timeAgo(p.created_at)}</div>
        </div>
      ))}
    </div>
    <NewsWidget />
    <div style={{ background: 'var(--card)', borderRadius: 20, border: '1px solid var(--border)', padding: '16px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <span style={{ color: '#2563eb' }}><Icon d={ICONS.calendar} size={16} /></span>
        <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>Upcoming Events</span>
      </div>
      {upcomingMeetings.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', padding: '12px 0' }}>No upcoming meetings</div>
      ) : upcomingMeetings.map((ev, i) => {
        const dt = new Date(ev.scheduled_at);
        const colors = ['#0d9488', '#2563eb', '#7c3aed', '#dc2626', '#d97706'];
        const color = colors[i % colors.length];
        const mon = dt.toLocaleDateString('en-IN', { month: 'short' }).toUpperCase();
        const day = dt.getDate();
        const time = dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
        return (
          <div key={ev.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', borderBottom: i < upcomingMeetings.length-1 ? '1px solid var(--border)' : 'none' }}>
            <div style={{ width: 42, height: 42, borderRadius: 12, background: color+'18', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 9, fontWeight: 800, color }}>{mon}</span>
              <span style={{ fontSize: 15, fontWeight: 800, color, lineHeight: 1 }}>{day}</span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.title}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{time} · {ev.organizer_name||'Qtalk'}</div>
            </div>
            {ev.meet_link&&(
              <a href={ev.meet_link.startsWith('http')?ev.meet_link:`https://qtalk.qualtechedge.in${ev.meet_link}`} target="_blank" rel="noreferrer"
                style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, background: color+'18', color, textDecoration: 'none', fontWeight: 700, flexShrink: 0 }}>Join</a>
            )}
          </div>
        );
      })}
    </div>
  </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────
export default function AnnouncementsPage() {
  const { user, darkMode } = useStore();
  const [posts, setPosts] = useState([]);
  const [pinned, setPinned] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('recent');
  const [search, setSearch] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchPosts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/announcements?filter=${filter}`, { headers: getAuthHeader() });
      setPosts(data); setPinned(data.filter(p => p.is_pinned));
      // Record a view for each post the user hasn't already viewed (deduped server-side)
      data.forEach(p => {
        if (!p.viewed_by_me) {
          axios.post(`${API_URL}/announcements/${p.id}/view`, {}, { headers: getAuthHeader() }).catch(() => {});
        }
      });
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);
  useEffect(() => {
    axios.get(`${API_URL}/users`, { headers: getAuthHeader() }).then(r => setUsers(r.data)).catch(() => {});
  }, []);

  // Listen for real-time notifications even when on announcements page
  useEffect(() => {
    let attempts = 0;
    const register = () => {
      const s = getSocket();
      if (!s) { if (attempts++ < 20) setTimeout(register, 300); return; }
      s.off('announcement_reaction');
      s.on('announcement_reaction', (data) => {
        toast(`❤️ ${data.body}`, { duration: 5000, icon: '❤️' });
        fetchPosts(); // Refresh like count
      });
      s.off('announcement_comment');
      s.on('announcement_comment', (data) => {
        toast(`💬 ${data.body}`, { duration: 5000, icon: '💬' });
        fetchPosts();
      });
      s.off('mentioned_in_announcement');
      s.on('mentioned_in_announcement', (data) => {
        toast(`🔔 ${data.body}`, { duration: 6000, icon: '🔔' });
      });
      s.off('new_announcement');
      s.on('new_announcement', (data) => {
        toast(`📢 ${data.poster_name} posted an announcement`, { duration: 5000 });
        fetchPosts();
      });
    };
    register();
    return () => {
      const s = getSocket();
      if (s) {
        s.off('announcement_reaction');
        s.off('announcement_comment');
        s.off('mentioned_in_announcement');
        s.off('new_announcement');
      }
    };
  }, [fetchPosts]);

  const handlePost = async (fd) => {
    await axios.post(`${API_URL}/announcements`, fd, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
    toast.success('Posted!'); fetchPosts();
  };

  const handlePin = async (id) => {
    try { await axios.put(`${API_URL}/announcements/${id}/pin`, {}, { headers: getAuthHeader() }); fetchPosts(); }
    catch(e) { toast.error('Failed'); }
  };

  const vars = darkMode ? { '--bg': '#0a0f1a', '--card': '#111827', '--card-inner': '#1f2937', '--border': 'rgba(255,255,255,0.07)', '--text': '#f1f5f9', '--text-secondary': '#94a3b8', '--muted': '#64748b', '--accent': '#0d9488' }
    : { '--bg': '#f1f5f9', '--card': '#ffffff', '--card-inner': '#f8fafc', '--border': 'rgba(0,0,0,0.07)', '--text': '#0f172a', '--text-secondary': '#475569', '--muted': '#94a3b8', '--accent': '#0d9488' };

  const filtered = posts.filter(p => !search || p.content?.toLowerCase().includes(search.toLowerCase()) || p.full_name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ ...vars, height: '100vh', overflow: 'hidden', background: 'var(--bg)', fontFamily: 'system-ui, sans-serif', display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: 'var(--card)', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px', height: 60, display: 'flex', alignItems: 'center', gap: 16 }}>
          <a href="/chat" style={{ width: 36, height: 36, borderRadius: 12, border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text)', textDecoration: 'none', background: 'var(--card-inner)', flexShrink: 0 }}>
            <Icon d={ICONS.back} size={16} />
          </a>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.3px' }}>Qtalk Announcements</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>Company-wide updates</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {showSearch
              ? <input autoFocus value={search} onChange={e => setSearch(e.target.value)} onBlur={() => { if (!search) setShowSearch(false); }} placeholder="Search..." style={{ padding: '8px 14px', borderRadius: 12, border: '1px solid var(--border)', background: 'var(--card-inner)', color: 'var(--text)', fontSize: 13, outline: 'none', width: 200 }} />
              : <button onClick={() => setShowSearch(true)} style={{ width: 36, height: 36, borderRadius: 12, border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', background: 'transparent', cursor: 'pointer' }}><Icon d={ICONS.search} size={16} /></button>}
            <Avatar name={user?.full_name} url={user?.avatar_url} size={36} />
          </div>
        </div>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px', display: 'flex', gap: 4 }}>
          {[['recent', 'Recent'], ['pinned', 'Pinned'], ['department', 'My Department']].map(([val, label]) => (
            <button key={val} onClick={() => setFilter(val)} style={{ padding: '10px 18px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: filter === val ? 700 : 500, color: filter === val ? 'var(--accent)' : 'var(--muted)', borderBottom: `2px solid ${filter === val ? 'var(--accent)' : 'transparent'}`, transition: 'all 0.2s' }}>{label}</button>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
      <div style={{ maxWidth: 1100, width: '100%', margin: '0 auto', padding: '24px', display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, alignItems: 'start', height: '100%' }}>
        <div style={{ overflowY: 'auto', height: '100%', paddingRight: 8, scrollbarWidth: 'thin', scrollbarColor: 'var(--border) transparent' }}>
          <Composer currentUser={user} onPost={handlePost} users={users} />
          {loading && <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>Loading...</div>}
          {!loading && filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--muted)' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📢</div>
              <div style={{ fontWeight: 600, color: 'var(--text)' }}>No announcements yet</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>Be the first to share something!</div>
            </div>
          )}
          {filtered.map(post => <PostCard key={post.id} post={post} currentUser={user} onPin={handlePin} onDelete={(id) => setPosts(p => p.filter(x => x.id !== id))} users={users} />)}
        </div>
        <div style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden', paddingRight: 4,
          scrollbarWidth: 'thin', scrollbarColor: 'var(--border) transparent' }}>
          <Sidebar pinned={pinned} />
        </div>
      </div>
      </div>
    </div>
  );
}
